import { Box, SxProps } from '@mui/material';
import { Button } from 'system/atoms';
import { ModalContainer, ModalProps } from 'components/Modals/ModalContainer';
import { SubscribersList, SubscribersListSkeleton } from './SubscribersList';
import { UserOrganizationSubscriptionItem } from '../types/UserOrganizationSubscriptionItem';
import { useState } from 'react';
import { Loading } from 'components/Loading';
import {
  useCreateCampaignSubscriptionMutation,
  useDeleteCampaignSubscriptionMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { ToastType, useToast } from 'services/toast';

const buttonsContainerSx: SxProps = {
  display: 'flex'
};

type ModalPropsWithoutTitles = Omit<ModalProps, 'title' | 'subtitle'>;

type Props = {
  modalProps: ModalPropsWithoutTitles;
  isUsersWithCampaignSubscriptionLoading: boolean;
  userOrganizations: UserOrganizationSubscriptionItem[];
  campaignId: string;
  organizationId: string;
  refetchUserOrganizationData: () => void;
};

export const SubscribersModal: React.FC<Props> = ({
  modalProps,
  isUsersWithCampaignSubscriptionLoading,
  userOrganizations,
  campaignId,
  organizationId,
  refetchUserOrganizationData
}) => {
  const { showToast } = useToast();

  const [createCampaignSubscription] = useCreateCampaignSubscriptionMutation();
  const [deleteCampaignSubscription] = useDeleteCampaignSubscriptionMutation();

  const [isMutating, setIsMutating] = useState(false);
  const [usersToToggle, setUserToToggle] = useState<string[]>([]);

  const handleToggleUser = (userId: string) => {
    if (isMutating) {
      return;
    }

    setUserToToggle((prevState) => {
      const items = [...prevState];

      if (items.includes(userId)) {
        return items.filter((id) => id !== userId);
      }

      return [...items, userId];
    });
  };

  const handleConfirm = async () => {
    setIsMutating(true);

    const updateMutations = usersToToggle.map((userId) => {
      const userOrganization = userOrganizations.find(
        (userOrganization) => userOrganization.user.id === userId
      );

      if (userOrganization) {
        const subscription =
          userOrganization.user.campaignSubscriptions?.items?.[0];

        if (!subscription) {
          return createCampaignSubscription({
            variables: {
              input: {
                campaignId,
                organizationId,
                userId
              }
            }
          });
        } else {
          return deleteCampaignSubscription({
            variables: {
              input: {
                id: subscription.id
              }
            }
          });
        }
      }
    });

    try {
      await Promise.all(updateMutations);

      showToast({
        message: "The campaign's team members' list has been updated",
        type: ToastType.Success
      });

      refetchUserOrganizationData();
      setUserToToggle([]);
      modalProps.onClose();
    } catch (error) {
      showToast({
        message: 'Could not update the team member subscription list',
        type: ToastType.Error
      });
    }

    setIsMutating(false);
  };

  const handleCancel = () => {
    setUserToToggle([]);
    modalProps.onClose();
  };

  return (
    <ModalContainer
      {...modalProps}
      title="Notify team members"
      subtitle="Tick the checkboxes and notify your team about this campaign via email."
    >
      {isUsersWithCampaignSubscriptionLoading && <SubscribersListSkeleton />}
      {!isUsersWithCampaignSubscriptionLoading && (
        <SubscribersList
          userOrganizations={userOrganizations}
          usersToToggle={usersToToggle}
          onToggleUser={handleToggleUser}
        />
      )}
      <Box sx={buttonsContainerSx}>
        <Button
          variant="primary"
          disabled={usersToToggle.length === 0 || isMutating}
          onClick={handleConfirm}
          endIcon={
            isMutating ? (
              <Loading sx={{ color: 'white' }} size={20} />
            ) : undefined
          }
        >
          Confirm
        </Button>
        <Button
          variant="tertiary"
          state="destructive"
          onClick={handleCancel}
          disabled={isMutating}
        >
          Cancel
        </Button>
      </Box>
    </ModalContainer>
  );
};
